import React from 'react';
import PropTypes from 'prop-types';
import IniIcon from '../../../../Assets/images/svg/ini.svg';
import AprIcon from '../../../../Assets/images/svg/apr.svg';
import TotalIcon from '../../../../Assets/images/svg/total.svg';
import ProgressIcon from '../../../../Assets/images/svg/progress.svg';
import EstIcon from '../../../../Assets/images/svg/est.svg';

const resolveIconSrc = (icon) => {
  if (typeof icon === 'string') return icon;
  if (icon && typeof icon === 'object' && typeof icon.default === 'string') return icon.default;
  return '';
};
const handleIconError = (event) => {
  event.currentTarget.style.display = 'none';
};

const formatNumeric = (value) => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => `${formatNumeric(value)}%`;

const getTmUtilizationColorClass = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  if (numericValue > 90) return 'green';
  if (numericValue >= 80) return 'yellow';
  return 'red';
};

const getEstEffortDeviationColorClass = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  if (numericValue > 50) return 'red';
  if (numericValue >= 20 && numericValue <= 50) return 'yellow';
  return 'green';
};

const renderFpRows = (hasUtilizationData, utilizationData, hasChangeRequestAccess) => (
  <>
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(IniIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label">Ini. est. effort (Hrs)</span>
      </span>
      <span className="util-value">
        {hasUtilizationData ? formatNumeric(utilizationData?.initialEstEffort) : '0.00'}
      </span>
    </div>
    {hasChangeRequestAccess && (
      <div className="utilization-row">
        <span className="util-label-group">
          <span className="icon"><img src={resolveIconSrc(AprIcon)} alt="" onError={handleIconError} /></span>
          <span className="util-label">CR Approval % (Hrs)</span>
        </span>
        <span className="util-value">
          {hasUtilizationData ? formatNumeric(utilizationData?.approvedCREffort) : '0.00'}
          {' '}
          (
          {hasUtilizationData ? formatPercent(utilizationData?.approvedCRPercentage) : '0.00%'}
          )
        </span>
      </div>
    )}
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(TotalIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label">Total est. effort (Hrs)</span>
      </span>
      <span className="util-value">
        {hasUtilizationData ? formatNumeric(utilizationData?.totalEstEffort) : '0.00'}
      </span>
    </div>
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(ProgressIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label">Cur. Act. Effort (Hrs)</span>
      </span>
      <span className="util-value">
        {hasUtilizationData ? formatNumeric(utilizationData?.currentActEffort) : '0.00'}
      </span>
    </div>
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(EstIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label">Est. effort deviation</span>
      </span>
      <span
        className={[
          'util-value',
          hasUtilizationData ? getEstEffortDeviationColorClass(utilizationData?.estEffortDeviation) : '',
        ].join(' ').trim()}
      >
        {hasUtilizationData ? formatPercent(utilizationData?.estEffortDeviation) : '0.00%'}
      </span>
    </div>
  </>
);

const renderTmRows = (hasUtilizationData, utilizationData, hasChangeRequestAccess) => (
  <>
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(IniIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label">Overall utilization</span>
      </span>
      <span
        className={[
          'util-value',
          hasUtilizationData ? getTmUtilizationColorClass(utilizationData?.overallUtilization) : '',
        ].join(' ').trim()}
      >
        {hasUtilizationData ? formatPercent(utilizationData?.overallUtilization) : '0.00%'}
      </span>
    </div>
    <div className="utilization-row">
      <span className="util-label-group">
        <span className="icon"><img src={resolveIconSrc(ProgressIcon)} alt="" onError={handleIconError} /></span>
        <span className="util-label-stack">
          <span className="util-label">Latest billed/utilization</span>
          {hasUtilizationData && utilizationData?.latestUtilizationDate
            ? <span className="util-sub-label">{utilizationData.latestUtilizationDate}</span>
            : null}
        </span>
      </span>
      <span
        className={[
          'util-value',
          hasUtilizationData ? getTmUtilizationColorClass(utilizationData?.latestUtilizationPercentage) : '',
        ].join(' ').trim()}
      >
        {hasUtilizationData ? formatPercent(utilizationData?.latestUtilizationPercentage) : '0.00%'}
      </span>
    </div>
    {hasChangeRequestAccess && (
      <div className="utilization-row">
        <span className="util-label-group">
          <span className="icon"><img src={resolveIconSrc(AprIcon)} alt="" onError={handleIconError} /></span>
          <span className="util-label">CR Approval % (Hrs)</span>
        </span>
        <span className="util-value">
          {hasUtilizationData ? formatNumeric(utilizationData?.approvedCREffort) : '0.00'}
          {' '}
          (
          {hasUtilizationData ? formatPercent(utilizationData?.approvedCRPercentage) : '0.00%'}
          )
        </span>
      </div>
    )}
  </>
);

const UtilizationCard = ({
  hasUtilizationData,
  utilizationData,
  projectTypeVariant,
  hasChangeRequestAccess = true,
}) => {
  const variant = projectTypeVariant || utilizationData?.mode || 'fp';

  return (
    <div className={`card utilization-card ${hasUtilizationData ? 'with-data' : 'no-data'}`}>
      <div className="card-header">
        <h3 className="card-title">{variant === 'tm' ? 'Utilization' : 'Effort'}</h3>
        <span className="divider" />
      </div>
      <div className="card-content">
        {variant === 'tm'
          ? renderTmRows(hasUtilizationData, utilizationData, hasChangeRequestAccess)
          : renderFpRows(hasUtilizationData, utilizationData, hasChangeRequestAccess)}
      </div>
    </div>
  );
};

export default UtilizationCard;

UtilizationCard.propTypes = {
  hasUtilizationData: PropTypes.bool.isRequired,
  projectTypeVariant: PropTypes.oneOf(['fp', 'tm', '']),
  hasChangeRequestAccess: PropTypes.bool,
  utilizationData: PropTypes.shape({
    mode: PropTypes.oneOf(['fp', 'tm']),
    initialEstEffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    approvedCREffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    approvedCRPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalEstEffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currentActEffort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estEffortDeviation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    overallUtilization: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    latestUtilizationPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    latestUtilizationDate: PropTypes.string,
  }),
};

UtilizationCard.defaultProps = {
  projectTypeVariant: '',
  hasChangeRequestAccess: true,
  utilizationData: null,
};
